// Interactive structure doors — proximity Open/Close (F), swinging leaf, and
// client collider mirror of server-authoritative open state.
//
// Seats/interact sibling: world.userData.doors → live leaf Groups (NOT part of
// structure consolidation) + soft proximity target on the action bar.
//
// F priority (shared with interact.ts): focused prop > near door > nothing.
// Door leaves never consolidate with the building shell so they can swing.

import * as THREE from "three";
import { toonMat } from "./toon.ts";
import { shade } from "./color.ts";
import { game, inputLocked } from "./state.ts";
import { chatTyping } from "./chat.ts";
import { getPropFocus } from "./focus.ts";
import { setDoorColliderOpen, type Collider } from "./physics.ts";
import { UNIT_BOX } from "./meshkit.ts";
import type { StructureDoor } from "./structure/define.ts";
import { sendDoor } from "./net.ts";

/** Match server DOOR_REACH (src/server/doors.ts). */
export const DOOR_REACH = 2.8;
/** Swing duration seconds (ease in/out). */
export const DOOR_SWING_S = 0.5;

export type WorldDoor = { id: string } & StructureDoor;

type LiveDoor = {
  id: string;
  spec: WorldDoor;
  /** Hinge pivot; leaf is a child offset along local +x. */
  pivot: THREE.Group;
  /** Authoritative open (from server / local predict). */
  open: boolean;
  /** Visual 0 = closed, 1 = open. */
  openT: number;
  /** Target openT we're easing toward. */
  targetT: number;
};

let doors: LiveDoor[] = [];
let colliders: Collider[] = [];
/** Soft-target nearest door in range (drives HUD). */
let nearId: string | null = null;

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/** Build a plank leaf Group with hinge at local origin, extending +x by w. */
function makeLeaf(w: number, h: number, color: number): THREE.Group {
  const g = new THREE.Group();
  const thick = 0.12;
  const wood = toonMat(color);
  const dark = toonMat(shade(color, -0.1));
  const iron = toonMat(0x3a3a42);

  const slab = new THREE.Mesh(UNIT_BOX, wood);
  slab.position.set(w / 2, h / 2, 0);
  slab.scale.set(w, h - 0.08, thick);
  slab.castShadow = true;
  slab.receiveShadow = false;
  g.add(slab);

  // Plank grooves
  for (let i = 1; i < 4; i++) {
    const strip = new THREE.Mesh(UNIT_BOX, dark);
    strip.position.set((w * i) / 4, h / 2, 0);
    strip.scale.set(0.04, h - 0.16, thick + 0.02);
    g.add(strip);
  }
  // Battens
  for (const fy of [0.28, 0.72]) {
    const b = new THREE.Mesh(UNIT_BOX, dark);
    b.position.set(w / 2, h * fy, 0);
    b.scale.set(w - 0.1, 0.1, thick + 0.03);
    g.add(b);
  }
  // Handle near free edge
  const handle = new THREE.Mesh(UNIT_BOX, iron);
  handle.position.set(w * 0.82, h * 0.48, thick * 0.6);
  handle.scale.set(0.1, 0.28, thick + 0.1);
  g.add(handle);
  // Hinge nubs at hinge edge
  for (const hy of [0.25, 0.55, 0.8]) {
    const n = new THREE.Mesh(UNIT_BOX, iron);
    n.position.set(0.06, h * hy, 0);
    n.scale.set(0.12, 0.12, thick + 0.08);
    g.add(n);
  }
  // Don't wall-fade with the shell (kept like the old doorLeaf).
  g.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).userData.cut = null;
  });
  return g;
}

function applyCollider(d: LiveDoor): void {
  // Mirror server authority: solid when closed, walkable when open. Swing is
  // purely cosmetic (`openT`); do NOT keep the box walkable mid-close — the
  // server solidifies immediately on close accept (see setDoorOpen).
  setDoorColliderOpen(colliders, d.id, d.open);
}

function setPivotRotation(d: LiveDoor): void {
  const t = easeInOut(Math.min(1, Math.max(0, d.openT)));
  d.pivot.rotation.y = d.spec.closedRy + t * d.spec.openDelta;
}

/** Register doors for the built world. Call after initWorld / swapWorld. */
export function setDoorWorld(world: THREE.Object3D, worldColliders: Collider[]): void {
  disposeDoors();
  colliders = worldColliders;
  const list = (world.userData.doors ?? []) as WorldDoor[];
  for (const spec of list) {
    const pivot = new THREE.Group();
    pivot.position.set(spec.hx, spec.hy, spec.hz);
    const leaf = makeLeaf(spec.w, spec.h, spec.color);
    pivot.add(leaf);
    world.add(pivot);
    const live: LiveDoor = {
      id: spec.id,
      spec,
      pivot,
      open: false,
      openT: 0,
      targetT: 0,
    };
    setPivotRotation(live);
    applyCollider(live);
    doors.push(live);
  }
}

/** Tear down leaf Groups (map swap / disposeWorld). */
export function disposeDoors(): void {
  for (const d of doors) {
    d.pivot.parent?.remove(d.pivot);
    // UNIT_BOX + toonMat caches are shared — do not dispose geos/materials.
  }
  doors = [];
  nearId = null;
  colliders = [];
}

/** Apply join sync — open ids only (default closed). */
export function applyDoorsSync(openIds: string[]): void {
  const open = new Set(openIds);
  for (const d of doors) {
    const want = open.has(d.id);
    d.open = want;
    d.targetT = want ? 1 : 0;
    // Snap if far from target (first paint / reconnect).
    if (Math.abs(d.openT - d.targetT) > 0.01) {
      d.openT = d.targetT;
      setPivotRotation(d);
      applyCollider(d);
    }
  }
}

/** Apply a single-door delta from the server. */
export function applyDoorDelta(id: string, open: boolean): void {
  const d = doors.find((x) => x.id === id);
  if (!d) return;
  d.open = open;
  d.targetT = open ? 1 : 0;
  // Collider follows `open` immediately (open or close) — same as the server.
  applyCollider(d);
}

function nearestDoor(): LiveDoor | null {
  if (game.down || !game.player) return null;
  const px = game.player.position.x;
  const pz = game.player.position.z;
  let best: LiveDoor | null = null;
  let bestD = DOOR_REACH;
  for (const d of doors) {
    const dist = Math.hypot(px - d.spec.x, pz - d.spec.z);
    if (dist < bestD) {
      bestD = dist;
      best = d;
    }
  }
  return best;
}

/** Action-bar slot state for combat.ts (Interact slot when no prop focus). */
export function doorSlotState(): {
  show: boolean;
  enabled: boolean;
  label: string;
  icon: string;
} {
  // Prop focus owns the Interact slot entirely.
  if (getPropFocus()) return { show: false, enabled: false, label: "", icon: "" };
  const d = nearId ? doors.find((x) => x.id === nearId) : null;
  if (!d) return { show: false, enabled: false, label: "", icon: "" };
  const label = d.open || d.targetT > 0.5 ? "Close" : "Open";
  const enabled = !game.down && !game.talking && !inputLocked();
  return { show: true, enabled, label, icon: "🚪" };
}

/** Toggle the soft-targeted door (F / slot click). Server is authoritative —
 *  we only send the intent; the `door` broadcast drives swing + collider. */
export function doDoor(): boolean {
  if (inputLocked()) return false;
  if (getPropFocus()) return false; // prop Interact owns F
  const d = nearId ? doors.find((x) => x.id === nearId) : nearestDoor();
  if (!d) return false;
  if (Math.hypot(game.player.position.x - d.spec.x, game.player.position.z - d.spec.z) > DOOR_REACH) {
    return false;
  }
  sendDoor(d.id);
  return true;
}

/** Per-frame: proximity soft-target + swing ease. Colliders track `open` only
 *  (set on applyDoorDelta / setDoorWorld) — not the visual swing. */
export function updateDoors(dt: number): void {
  const near = nearestDoor();
  nearId = near?.id ?? null;

  const rate = 1 / DOOR_SWING_S;
  for (const d of doors) {
    if (d.openT === d.targetT) continue;
    if (d.openT < d.targetT) d.openT = Math.min(d.targetT, d.openT + dt * rate);
    else d.openT = Math.max(d.targetT, d.openT - dt * rate);
    setPivotRotation(d);
  }
}

/** Wire F: call from interact keydown when prop doesn't handle it.
 *  Returns true if a door consumed the key. */
export function tryDoorHotkey(): boolean {
  if (chatTyping()) return false;
  return doDoor();
}
